import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Avatar,
  Box,
  Chip,
  Collapse,
  IconButton,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import PageLayout from '@shared/shell/Layout/PageLayout';
import { format } from 'date-fns';
import { APP_CONFIG } from '../../../../config/app.config';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Paper } from '@shared/ui/common/paper';
import ProductsFilter from '@shared/ui/filters/ProductsFilter';
import { useProductsFilter } from '@features/products/hooks/useProductsFilter';
import { MutationType } from '@domain/inventory/enums/MutationType.enum';
import ActiveFilters from '@shared/ui/filters/ActiveFilters';

const WarehouseInventoryMutations = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const [search, setSearch] = React.useState<string | undefined>(undefined);

  const { data: mutationsResponse, isFetching } = useQuery({
    queryKey: ['warehouse-mutations', id, page, rowsPerPage, search],
    queryFn: async () => {
      const response = await container.inventoryRepository.getInventoryMutations(id!, page + 1, rowsPerPage, search);
      if (response.status === 'error') throw new Error(response.error.message);
      return response.data;
    },
    enabled: !!id
  });

  const {
    filterCriteria,
    updateFilter,
  } = useProductsFilter();

  React.useEffect(() => {
    setSearch(filterCriteria.search || undefined);
    setPage(0);
  }, [filterCriteria.search]);

  const [expandedMutations, setExpandedMutations] = React.useState<Record<string, boolean>>({});

  const toggleMutation = (mutationId: string) => {
    setExpandedMutations(prev => ({
      ...prev,
      [mutationId]: !prev[mutationId]
    }));
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const groupedMutations = React.useMemo(() => {
    if (!mutationsResponse?.data) return [];

    const groups = mutationsResponse.data.reduce((acc, mutation) => {
      const date = format(new Date(mutation.date), 'yyyy-MM-dd');
      if (!acc[date]) acc[date] = [];
      acc[date].push(mutation);
      return acc;
    }, {} as Record<string, typeof mutationsResponse.data>);

    return Object.entries(groups)
      .map(([date, mutations]) => ({ date, mutations }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [mutationsResponse?.data]);

  return (
    <PageLayout backAction={() => navigate(`/inventory/warehouses/${id}`)} title="Inventory Mutations">
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <ProductsFilter
            onFilterChange={updateFilter}
            buttonLabel="Filter Products"
            filterCriteria={filterCriteria}
          />

          <ActiveFilters filterCriteria={filterCriteria} updateFilter={updateFilter} />
        </Stack>

        <Paper sx={{ position: 'relative' }}>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={50} />
                  <TableCell>Date</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>

                {groupedMutations.map(({ date, mutations }) => (
                  <React.Fragment key={date}>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ backgroundColor: 'grey.100' }}>
                        <Typography variant="subtitle1">
                          {format(new Date(date), 'PPP')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {mutations.map((mutation) => (
                      <React.Fragment key={mutation.id}>
                        <TableRow
                          onClick={() => mutation.warehouseConnection && toggleMutation(mutation.id)}
                          sx={{
                            cursor: mutation.warehouseConnection ? 'pointer' : 'default',
                            '&:hover': {
                              backgroundColor: mutation.warehouseConnection ? 'action.hover' : 'inherit'
                            }
                          }}
                        >
                          <TableCell>
                            {mutation.warehouseConnection && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleMutation(mutation.id);
                                }}
                              >
                                {expandedMutations[mutation.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell>{format(new Date(mutation.date), 'p')}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar src={mutation.product.image} variant="square" sx={{ width: 24, height: 24, borderRadius: 1, border: 1, borderColor: APP_CONFIG.ui.theme.colors.border }} />
                              <Typography>{mutation.product.title}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell> {mutation.type === MutationType.INCOMING ? '+' : mutation.type === MutationType.SYNC ? '=' : '-'} {mutation.quantity}</TableCell>
                          <TableCell>{mutation.referenceType} {mutation.type}</TableCell>
                        </TableRow>
                        {mutation.warehouseConnection && (
                          <TableRow>
                            <TableCell colSpan={6} style={{ padding: 0 }}>
                              <Collapse in={expandedMutations[mutation.id]}>
                                <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                  <Stack spacing={2}>
                                    <Typography variant="subtitle2">Warehouse Connection Details</Typography>
                                    <Stack direction="row" spacing={2}>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Type</Typography>
                                        <Typography>{mutation.warehouseConnection.mutationType}</Typography>
                                      </Box>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Source</Typography>
                                        <Typography>{mutation.warehouseConnection.sourceType}</Typography>
                                      </Box>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Active From</Typography>
                                        <Typography>{mutation.warehouseConnection.activeFrom ? format(mutation.warehouseConnection.activeFrom, 'PPP') : 'N/A'}</Typography>
                                      </Box>
                                      {mutation.warehouseConnection.activeUntil && (
                                        <Box>
                                          <Typography variant="caption" color="text.secondary">Active To</Typography>
                                          <Typography>{mutation.warehouseConnection.activeUntil ? format(mutation.warehouseConnection.activeUntil, 'PPP') : 'N/A'}</Typography>
                                        </Box>
                                      )}
                                    </Stack>
                                    {mutation.warehouseConnection.rules.length > 0 && (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Rules</Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                          {mutation.warehouseConnection.rules.map((rule, index) => (
                                            <Chip
                                              key={index}
                                              size="small"
                                              label={`${rule.field} ${rule.operator} ${Array.isArray(rule.value) ? rule.value.join(', ') : rule.value}`}
                                            />
                                          ))}
                                        </Stack>
                                      </Box>
                                    )}
                                  </Stack>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}

                {isFetching ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ py: 4 }}>
                      <Stack p={4} direction="row" justifyContent="center" alignItems="center">
                        <CircularProgress />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : groupedMutations.length === 0 && mutationsResponse?.total === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ py: 4 }}>
                      <Stack p={4} direction="row" justifyContent="center" alignItems="center">
                        <Typography variant="body1">No mutations found</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[50, 100, 200]}
            component="div"
            count={mutationsResponse?.total || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Stack>
    </PageLayout>
  );
};

export default WarehouseInventoryMutations; 